/**
 * Project Analytics — presentation config (colour bands).
 *
 * The four release-map bands and their colours, keyed by the band key the
 * middleware returns per cell (comfortable | marginal | below | none). This is
 * UI config, not data — all values/bands come from /projectAnalytics/* .
 * Band semantics are defined in the FSD (MOSYMPD-451 §5).
 */
export const RELEASE_BANDS = {
  comfortable: { label: 'Comfortable margin',      color: '#16a34a', bg: '#dcfce7', text: '#15803d' },
  // Amber, not blue: "near limit" is a caution, so it sits between the green
  // comfortable band and the red below-threshold band.
  marginal:    { label: 'Marginal / near limit',   color: '#F59E0B', bg: '#FEF3C7', text: '#B45309' },
  below:       { label: 'Below threshold',         color: '#dc2626', bg: '#fee2e2', text: '#b91c1c' },
  // Grey covers two distinct conditions that the FSD keeps separate, and the
  // middleware sends a different key for each:
  //   not_in_panel — the STAGE does not run Purity at all (FSD §5 "Grey is
  //                  shown where a stage does not run Purity ... and carries no
  //                  comparison to a specification"). This is the one the legend
  //                  names.
  //   none         — no band could be derived for a metric the stage DOES run:
  //                  an unparseable registered spec, or a flagged source data
  //                  issue (>1 qualifying result, shown unbanded per the FSD).
  // Both were previously resolving to `none` only because bandStyleOf fell back
  // to it for an unknown key — declared explicitly here so the mapping is
  // intentional and a future band rename cannot silently grey out a real result.
  not_in_panel: { label: 'Not in release panel',   color: '#9ca3af', bg: '#f3f4f6', text: '#6b7280' },
  none:         { label: 'No band derived',        color: '#9ca3af', bg: '#f3f4f6', text: '#6b7280' },
};

export const bandStyleOf = (bandKey) => {
  const b = RELEASE_BANDS[bandKey] || RELEASE_BANDS.none;
  return { background: b.bg, color: b.text };
};

// Legend entries, in band order. `none` is deliberately omitted — it is an
// internal "no band could be derived" state, not a category the reviewer reads
// off the chart, and it renders identically to not_in_panel.
export const RELEASE_LEGEND_KEYS = ['comfortable', 'marginal', 'below', 'not_in_panel'];

// Assay carries no registered specification limit and per FSD §5 is NEVER
// colour-banded — every Assay cell is a uniform white fill with a black
// border, regardless of value. Use this instead of bandStyleOf() for Assay.
export const ASSAY_CELL_STYLE = { background: '#ffffff', color: '#000000', border: '1px solid #000000' };

export default RELEASE_BANDS;
